'use client';

import Link from 'next/link';
import { useState } from 'react';
import { Menu, X, Shield, Handshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/theme-toggle';

const navLinks = [
  { label: 'Funktionen', href: '/#prozess' },
  { label: 'Preise', href: '/pricing' },
];

const verticalLinks = [
  { label: 'Versicherung', href: '/versicherung', icon: Shield },
  { label: 'M&A', href: '/m-a', icon: Handshake },
];

export function PublicNavbar({ isAuthenticated = false }: { isAuthenticated?: boolean }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="container mx-auto grid h-16 grid-cols-[1fr_auto_1fr] items-center px-4">
        <Link href="/" className="flex items-center gap-2 justify-self-start">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground text-sm font-bold">
            SB
          </div>
          <span className="text-lg font-bold text-foreground">
            salesbriefing.de
          </span>
        </Link>

        {/* Centered vertical chooser (desktop) */}
        <nav className="hidden items-center gap-2 justify-self-center lg:flex">
          {verticalLinks.map((v) => (
            <Link key={v.href} href={v.href}>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 border-accent/30 font-medium hover:border-accent hover:bg-accent/10 hover:text-accent"
              >
                <v.icon className="h-4 w-4 text-accent" />
                {v.label}
              </Button>
            </Link>
          ))}
        </nav>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-4 justify-self-end md:flex">
          {navLinks.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {l.label}
            </Link>
          ))}
          <ThemeToggle />
          {isAuthenticated ? (
            <Link href="/dashboard">
              <Button variant="accent" size="sm">
                Zum Dashboard
              </Button>
            </Link>
          ) : (
            <>
              <Link href="/login">
                <Button variant="ghost" size="sm">
                  Login
                </Button>
              </Link>
              <Link href="/register">
                <Button variant="accent" size="sm">
                  Kostenlos testen
                </Button>
              </Link>
            </>
          )}
        </nav>

        {/* Mobile toggle */}
        <button
          className="justify-self-end md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menü öffnen"
        >
          {mobileOpen ? (
            <X className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="border-t border-border bg-card px-4 pb-4 md:hidden">
          <nav className="flex flex-col gap-3 pt-3">
            <div className="grid grid-cols-2 gap-2">
              {verticalLinks.map((v) => (
                <Link key={v.href} href={v.href} onClick={() => setMobileOpen(false)}>
                  <Button variant="outline" size="sm" className="w-full gap-1.5">
                    <v.icon className="h-4 w-4 text-accent" />
                    {v.label}
                  </Button>
                </Link>
              ))}
            </div>
            {navLinks.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                className="text-sm font-medium text-muted-foreground"
                onClick={() => setMobileOpen(false)}
              >
                {l.label}
              </Link>
            ))}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Design</span>
              <ThemeToggle />
            </div>
            {isAuthenticated ? (
              <Link href="/dashboard" onClick={() => setMobileOpen(false)}>
                <Button variant="accent" size="sm" className="w-full">
                  Zum Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link href="/login" onClick={() => setMobileOpen(false)}>
                  <Button variant="ghost" size="sm" className="w-full">
                    Login
                  </Button>
                </Link>
                <Link href="/register" onClick={() => setMobileOpen(false)}>
                  <Button variant="accent" size="sm" className="w-full">
                    Kostenlos testen
                  </Button>
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
